import React, {useState, useEffect, useRef, useContext, useCallback} from 'react';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {Message, getMessage, svgs} from 'epic-diesel-common';
import influencerActions from '../../../../actions/influencer.actions';
import Input from 'epic-diesel-form/src/components/form/Input';
import {isValidCreatorCode} from '../../../../../shared/utils/ValidationUtils';
import {selectCodeAvailability, selectMessages, selectDisplayName} from '../../../../selectors';
import {ApplicationContext} from '../../../../hocs/WithApplicationView';

//@ts-ignore
import styles from './CodeSelfServiceSection.pcss';

interface Props {
    updateForm: (creatorCode: string) => void;
    creatorCode: string;
}

const CODES = {
    header: 'epic.affiliate.settings.link_title.v2',
    title: 'epic.affiliate.ccss.reqs',
    codeTaken: 'epic.affiliate.ccss.alreadyTaken',
    creatorCode: 'epic.affiliate.common.creatorCode',
    displayName: 'epic.affiliate.common.displayName',
    unavailable: 'errors.com.epicgames.validation.unavailable',
    required: 'errors.com.epicgames.validation.required_field.v2',
    reqs: [
        {code: 'errors.com.epicgames.validation.minMaxLength', args: [3, 16]},
        {code: 'errors.com.epicgames.validation.alphanumeric'},
        {code: 'errors.com.epicgames.validation.similarToBrand'},
        {code: 'errors.com.epicgames.validation.communityRules'}
    ]
};

const CodeSelfServiceSection: React.FC<Props> = props => {
    const context = useContext(ApplicationContext);
    const inputRef = useRef<HTMLInputElement>();
    const dispatch = useDispatch();
    const messages = useSelector(selectMessages);
    const displayName = useSelector(selectDisplayName);
    const codeAvailability = useSelector(selectCodeAvailability, shallowEqual);
    const [timer, setTimer] = useState<number>(null);

    const {schema} = context;
    const {loading, slug, available} = codeAvailability || {};
    const checkedCode = slug === (props.creatorCode || '').toLowerCase();

    useEffect(() => {
        if (slug && inputRef && inputRef.current) {
            inputRef.current.focus();
        }

        return () => clearTimeout(timer);
    }, [slug]);

    const onChange = useCallback((event, timer) => {
        const {value: code} = event.currentTarget;
        clearTimeout(timer);
        props.updateForm(code);
        setTimer(
            setTimeout(() => {
                if (!isValidCreatorCode(code)) {
                    return;
                }

                dispatch(influencerActions.checkCodeAvailability(code));
            }, 1000)
        );
    }, []);

    const renderInputIcon = () => {
        let icon, iconClassName;

        if (loading) {
            icon = svgs.LOADING;
        }

        if (checkedCode) {
            if (available) {
                icon = svgs.CHECK;
                iconClassName = styles.available;
            } else {
                icon = svgs.CLOSE;
                iconClassName = styles.hasError;
            }
        }

        return {icon, iconClassName};
    };

    return (
        <div className={styles.codeSelfServiceSection}>
            <h2>
                <Message code={CODES.header} /> {' *'}
            </h2>
            <p>
                <Message code={CODES.title} />
            </p>
            <ul className={styles.creatorCodeRules}>
                {CODES.reqs.map((value, i) => {
                    return (
                        <li key={i}>
                            <Message {...value} />
                        </li>
                    );
                })}
            </ul>

            {displayName && (
                <div className={styles.displayNameContainer}>
                    <Message code={CODES.displayName} />
                    <span className={styles.displayName}>{` ${displayName}`}</span>
                </div>
            )}

            <div className={styles.fields}>
                <div className={styles.field}>
                    <Input
                        ref={inputRef}
                        required
                        maxLength={16}
                        disabled={loading}
                        autoComplete="off"
                        name={schema.codeSelfService.identifier}
                        value={props.creatorCode}
                        placeholder={schema.codeSelfService.display.text}
                        validations={schema.codeSelfService.validations}
                        validationErrors={schema.codeSelfService.validationMessages}
                        onChange={e => onChange(e, timer)}
                        defaultErrorMessage={getMessage(messages, CODES.required)}
                        {...renderInputIcon()}
                    />

                    {!available && checkedCode && (
                        <Message
                            className={styles.hasError}
                            code={CODES.unavailable}
                            args={[
                                getMessage(messages, CODES.creatorCode),
                                String(props.creatorCode).toUpperCase()
                            ]}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(CodeSelfServiceSection);
